// syntax-marking.js — "mark the constituents" exercise.
//
// Every word of the sentence is clickable and looks alike: the student drags
// across the words that form a unit and then picks a label. Nothing in the
// page says how many units there are, which words belong together, or which
// labels are in play — the palette is authored and may hold labels that never
// occur. Checking costs one of a small number of attempts and reports only
// which of the student's own units are wrong, never what the right answer is,
// so the exercise cannot be probed one guess at a time.
//
// Markup comes from the syntax-marking.lua filter.

// Label text comes from the author's labels= list; set it as text, not markup.
function textSpan(className, text) {
    const span = document.createElement('span');
    span.className = className;
    span.textContent = text;
    return span;
}

const READY = 'data-ph-sm-ready';

// atob yields a byte string; the payload is UTF-8. Decoding it this way keeps
// the widget working where TextDecoder is absent (older WebViews, jsdom).
function utf8FromBinary(bin) {
    let escaped = '';
    for (let i = 0; i < bin.length; i += 1) {
        escaped += '%' + bin.charCodeAt(i).toString(16).padStart(2, '0');
    }
    try {
        return decodeURIComponent(escaped);
    } catch (_) {
        return bin;
    }
}

function decodeSolution(root) {
    try {
        return JSON.parse(utf8FromBinary(atob(root.dataset.solution || '')));
    } catch (err) {
        console.error('[syntax-marking] undecodable solution', err);
        return { items: [] };
    }
}

function assignKeys(labels) {
    // One keyboard shortcut per label: first free letter of its code.
    const used = new Set();
    return labels.map(([code]) => {
        for (const ch of code.toLowerCase()) {
            if (/[a-z]/.test(ch) && !used.has(ch)) {
                used.add(ch);
                return ch;
            }
        }
        return null;
    });
}

class SyntaxMarking {
    constructor(root) {
        this.root = root;
        this.labels = JSON.parse(root.dataset.labels || '[]');
        this.keys = assignKeys(this.labels);
        this.maxAttempts = parseInt(root.dataset.attempts || '2', 10) || 2;
        this.solution = decodeSolution(root);

        this.items = Array.from(root.querySelectorAll('.ph-sm-item')).map((el, index) => ({
            el,
            index,
            tokens: Array.from(el.querySelectorAll('.ph-sm-tok')),
            feedback: el.querySelector('.ph-sm-feedback'),
            units: [],
            attempts: 0,
            done: false,
            checked: false,
        }));

        this.pending = null; // { item, from, to }
        this.buildPalette();
        this.items.forEach((item) => this.buildItem(item));
        this.bindKeyboard();

        if (root.dataset.reveal === '1') {
            this.items.forEach((item) => this.reveal(item, true));
        }
    }

    // ── chrome ────────────────────────────────────────────────────────────
    buildPalette() {
        const bar = document.createElement('div');
        bar.className = 'ph-sm-palette';

        const hint = document.createElement('p');
        hint.className = 'ph-sm-palette-hint';
        hint.innerHTML =
            '<strong>Auswählen:</strong> ein Wort anklicken; für mehrere ziehen oder ' +
            '<strong>Shift+Klick</strong> auf das letzte. Ein Klick auf ein gesetztes ' +
            'Kürzel wählt seine Wörter wieder aus.<br>' +
            '<strong>Etikettieren:</strong> Etikett anklicken oder den Buchstaben tippen. ' +
            'Ein anderes Etikett ersetzt es, dasselbe nimmt es weg. Die Auswahl bleibt ' +
            'dabei stehen — auch für ein Etikett <em>innerhalb</em> eines anderen.';
        bar.appendChild(hint);

        const chipRow = document.createElement('div');
        chipRow.className = 'ph-sm-chips';
        bar.appendChild(chipRow);

        this.chips = this.labels.map(([code, name], i) => {
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = `ph-sm-chip ${this.colourOf(code)}`;
            chip.dataset.code = code;
            chip.disabled = true;
            chip.title = this.keys[i] ? `${name} — Taste ${this.keys[i].toUpperCase()}` : name;
            // The sentence is tagged with the short code, so the chip has to
            // teach the code, not only the word behind it.
            chip.append(textSpan('ph-sm-chip-code', code), textSpan('ph-sm-chip-name', name));
            if (this.keys[i]) chip.append(textSpan('ph-sm-chip-key', this.keys[i].toUpperCase()));
            chip.addEventListener('click', () => this.assign(code));
            chipRow.appendChild(chip);
            return chip;
        });

        this.root.insertBefore(bar, this.root.firstChild);
    }

    buildItem(item) {
        const bar = document.createElement('div');
        bar.className = 'ph-sm-actions';

        item.checkBtn = document.createElement('button');
        item.checkBtn.type = 'button';
        item.checkBtn.className = 'ph-sm-btn';
        item.checkBtn.textContent = 'Prüfen';
        item.checkBtn.addEventListener('click', () => this.check(item));
        bar.appendChild(item.checkBtn);

        item.attemptsLeft = document.createElement('span');
        item.attemptsLeft.className = 'ph-sm-attempts';
        item.attemptsLeft.textContent = `${this.maxAttempts} Versuche`;
        bar.appendChild(item.attemptsLeft);

        item.el.appendChild(bar);
        item.actions = bar;

        const sentence = item.el.querySelector('.ph-sm-sentence');
        this.bindSelection(item, sentence);
    }

    // ── selection ─────────────────────────────────────────────────────────
    //
    // Exactly one rule per gesture, and none of them depends on what is
    // already marked:
    //
    //   click a word            → the selection is that word
    //   drag / Shift+click      → the selection grows to that word
    //   click a placed label    → the selection is that unit's words
    //   Escape                  → nothing is selected
    //
    // A selection is never implicitly resized, and a unit is never picked up
    // by accident. The unit whose words are *exactly* the selection is the
    // active one, and that is the only thing a label acts on.
    bindSelection(item, sentence) {
        const tokenAt = (x, y) => {
            const el = document.elementFromPoint(x, y);
            const tok = el && el.closest ? el.closest('.ph-sm-tok') : null;
            return tok && sentence.contains(tok) ? tok : null;
        };

        let dragging = false;

        const pick = (i, extend) => {
            if (extend && this.pending && this.pending.item === item) {
                this.extendTo(item, i);
            } else {
                this.pending = { item, from: i, to: i, anchor: i };
            }
            this.render(item);
        };

        const onDown = (e) => {
            if (item.done) return;

            const tag = e.target.closest && e.target.closest('.ph-sm-tag');
            if (tag) {
                e.preventDefault();
                this.pending = {
                    item,
                    from: Number(tag.dataset.from),
                    to: Number(tag.dataset.to),
                    anchor: Number(tag.dataset.from),
                };
                this.render(item);
                return;
            }

            const tok = e.target.closest && e.target.closest('.ph-sm-tok');
            if (!tok) return;
            // preventDefault stops the browser's own text selection, which also
            // stops the click from moving focus — so move it by hand, or the
            // keyboard shortcuts would only work after a Tab.
            e.preventDefault();
            if (tok.focus) tok.focus({ preventScroll: true });
            dragging = true;
            pick(Number(tok.dataset.i), e.shiftKey);
        };

        const onMove = (e) => {
            if (!dragging || !this.pending || this.pending.item !== item) return;
            const tok = tokenAt(e.clientX, e.clientY);
            if (!tok) return;
            const i = Number(tok.dataset.i);
            if (i !== this.pending.to || i !== this.pending.from) {
                this.extendTo(item, i);
                this.render(item);
            }
        };

        const onUp = () => { dragging = false; };

        sentence.addEventListener('pointerdown', onDown);
        sentence.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);

        // Keyboard mirrors the mouse exactly: Enter is a click, Shift+Enter is
        // a Shift+click.
        sentence.addEventListener('keydown', (e) => {
            if (item.done) return;
            const tok = e.target.closest && e.target.closest('.ph-sm-tok');
            if (!tok) return;
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                pick(Number(tok.dataset.i), e.shiftKey);
            }
        });
    }

    extendTo(item, i) {
        const p = this.pending;
        const anchor = p.anchor === undefined ? p.from : p.anchor;
        p.anchor = anchor;
        const from = Math.min(anchor, i);
        const to = Math.max(anchor, i);

        // Units may sit inside one another (Prädikatsnomen inside Prädikat) but
        // never half-overlap, so a selection that would cut a unit in two is
        // simply not taken.
        const cuts = item.units.some((u) => {
            if (u.from > to || u.to < from) return false;
            const nested = (u.from >= from && u.to <= to) || (from >= u.from && to <= u.to);
            return !nested;
        });
        if (cuts) return;

        p.from = from;
        p.to = to;
    }

    // The unit that the selection is currently on, if any.
    activeUnit() {
        const p = this.pending;
        if (!p) return null;
        return p.item.units.find((u) => u.from === p.from && u.to === p.to) || null;
    }

    assign(code) {
        const p = this.pending;
        if (!p || p.item.done) return;
        const item = p.item;
        const active = this.activeUnit();

        // Same label again: take it off. The words stay selected, so another
        // label can go on straight away.
        if (active && active.label === code) {
            item.units = item.units.filter((u) => u !== active);
            item.checked = false;
            this.render(item);
            return;
        }

        // A label never sits inside itself. Marking a wider or narrower range
        // with a label that is already there moves the boundary instead of
        // stacking a second unit on top of the first — which is how a
        // mis-drawn boundary gets corrected.
        item.units = item.units.filter((u) => {
            if (u === active || u.label !== code) return true;
            const nested = (u.from >= p.from && u.to <= p.to) || (p.from >= u.from && p.to <= u.to);
            return !nested;
        });

        if (active) {
            active.label = code;
            delete active.verdict;
        } else {
            item.units.push({ from: p.from, to: p.to, label: code });
        }
        item.units.sort((a, b) => (a.from - b.from) || (b.to - a.to));
        item.checked = false;
        this.render(item);
    }

    removeActive() {
        const active = this.activeUnit();
        if (!active) return;
        const item = this.pending.item;
        item.units = item.units.filter((u) => u !== active);
        item.checked = false;
        this.render(item);
    }

    bindKeyboard() {
        this.root.addEventListener('keydown', (e) => {
            if (!this.pending || e.metaKey || e.ctrlKey || e.altKey) return;
            const idx = this.keys.indexOf(e.key.toLowerCase());
            if (idx >= 0) {
                e.preventDefault();
                this.assign(this.labels[idx][0]);
            } else if (e.key === 'Backspace' || e.key === 'Delete') {
                e.preventDefault();
                this.removeActive();
            } else if (e.key === 'Escape') {
                const item = this.pending.item;
                this.pending = null;
                this.render(item);
            }
        });
    }

    // ── rendering ─────────────────────────────────────────────────────────
    // Subjekt red, Prädikat blue, Objekt green, Adverbiale amber, Attribut
    // violet: the role comes from the filter (third field of each label), so
    // the colours match the printed key and the classroom convention. A code
    // the filter does not know keeps the positional palette.
    colourOf(code) {
        const i = this.labels.findIndex(([c]) => c === code);
        const role = i >= 0 ? this.labels[i][2] : '';
        if (role) return `ph-sm-role-${role}`;
        return `ph-sm-c${((i < 0 ? 0 : i) % 8) + 1}`;
    }

    // A unit is drawn as one continuous run — including the spaces between its
    // words — so that "Domina et serva" reads as one thing on the page and not
    // as three marked words standing next to each other.
    wrapRun(item, from, to, className) {
        const first = item.tokens[from];
        const last = item.tokens[to];
        if (!first || !last) return null;
        const run = document.createElement('span');
        run.className = className;
        first.parentNode.insertBefore(run, first);
        let node = first;
        while (node) {
            const next = node.nextSibling;
            run.appendChild(node);
            if (node === last) break;
            node = next;
        }
        return run;
    }

    render(item) {
        // Wrapping a run moves nodes, and moving the focused token blurs it —
        // which would silently kill the keyboard shortcuts after every label.
        const focused = item.tokens.includes(document.activeElement)
            ? document.activeElement : null;

        item.el.querySelectorAll('.ph-sm-run').forEach((run) => {
            const parent = run.parentNode;
            while (run.firstChild) parent.insertBefore(run.firstChild, run);
            run.remove();
        });
        item.el.querySelectorAll('.ph-sm-tag').forEach((el) => el.remove());
        item.tokens.forEach((tok) => {
            tok.className = 'ph-sm-tok' + (tok.classList.contains('ph-tok-glue') ? ' ph-tok-glue' : '');
        });

        const pending = (this.pending && this.pending.item === item) ? this.pending : null;
        const active = pending ? this.activeUnit() : null;

        // The selection is drawn by the same machinery as the units, so that a
        // selection around or inside an existing unit nests properly instead of
        // fighting it for the same words.
        const runs = item.units.map((u) => ({ unit: u, from: u.from, to: u.to }));
        if (pending && !active) {
            runs.push({ unit: null, from: pending.from, to: pending.to });
        }
        runs.sort((a, b) => (a.from - b.from) || ((b.to - b.from) - (a.to - a.from)));

        runs.forEach((r) => {
            if (!r.unit) {
                this.wrapRun(item, r.from, r.to, 'ph-sm-run ph-sm-run-pending');
                return;
            }

            const u = r.unit;
            const colour = this.colourOf(u.label);
            for (let i = u.from; i <= u.to; i++) {
                if (item.tokens[i]) item.tokens[i].classList.add('ph-sm-in');
            }
            const verdict = u.verdict ? ` ph-sm-${u.verdict}` : '';
            const isActive = (u === active) ? ' ph-sm-run-active' : '';
            const inner = item.units.some((o) => o !== u
                && o.from <= u.from && o.to >= u.to) ? ' ph-sm-run-inner' : '';
            const run = this.wrapRun(item, u.from, u.to,
                `ph-sm-run ${colour}${verdict}${isActive}${inner}`);
            if (!run) return;

            const tag = document.createElement('sup');
            tag.className = `ph-sm-tag ${colour}${verdict}`;
            tag.dataset.from = String(u.from);
            tag.dataset.to = String(u.to);
            tag.title = 'Diese Einheit auswählen';
            tag.textContent = u.verdict
                ? `${u.label} ${u.verdict === 'ok' ? '✓' : '✗'}`
                : u.label;
            run.after(tag);
        });

        if (focused && focused.focus) focused.focus({ preventScroll: true });
        this.syncChips();
    }

    syncChips() {
        const active = Boolean(this.pending && !this.pending.item.done);
        const unit = this.activeUnit();
        const current = unit ? unit.label : null;
        this.chips.forEach((chip) => {
            chip.disabled = !active;
            chip.classList.toggle('ph-sm-chip-current', chip.dataset.code === current);
            chip.setAttribute('aria-pressed', chip.dataset.code === current ? 'true' : 'false');
        });
    }

    say(item, text, tone) {
        item.feedback.textContent = text;
        item.feedback.className = `ph-sm-feedback${tone ? ' ph-sm-' + tone : ''}`;
    }

    // ── checking ──────────────────────────────────────────────────────────
    check(item) {
        if (item.done) return;
        const key = (this.solution.items || [])[item.index] || [];
        item.attempts += 1;

        let hits = 0;
        item.units.forEach((u) => {
            const match = key.some(([i, j, label]) => i === u.from && j === u.to && label === u.label);
            u.verdict = match ? 'ok' : 'bad';
            if (match) hits += 1;
        });

        const wrong = item.units.length - hits;
        const complete = hits === key.length;

        // A finished item is no longer editable, so it must not keep a live
        // selection — settle that before drawing, not after.
        if (wrong === 0 && complete) {
            item.done = true;
            if (this.pending && this.pending.item === item) this.pending = null;
        }
        this.render(item);

        if (item.done) {
            this.say(item, item.attempts === 1 ? 'Stimmt.' : 'Jetzt stimmt es.', 'ok');
            item.actions.innerHTML = '';
            return;
        }

        const left = this.maxAttempts - item.attempts;
        if (wrong === 0) {
            // Deliberately no count: knowing how many are missing would let the
            // rest be filled in by elimination.
            this.say(item, 'Was du markiert hast, stimmt — aber der Satz ist noch nicht ganz zerlegt.', 'warn');
        } else {
            this.say(item, 'Die mit ✗ markierten Einheiten stimmen nicht. Prüfe Grenze und Etikett.', 'bad');
        }

        if (left > 0) {
            item.attemptsLeft.textContent = left === 1 ? 'noch 1 Versuch' : `noch ${left} Versuche`;
        } else {
            item.attemptsLeft.textContent = '';
            item.checkBtn.remove();
            const solve = document.createElement('button');
            solve.type = 'button';
            solve.className = 'ph-sm-btn ph-sm-btn-quiet';
            solve.textContent = 'Lösung zeigen';
            solve.addEventListener('click', () => this.reveal(item));
            item.actions.appendChild(solve);
        }
    }

    reveal(item, silent) {
        const key = (this.solution.items || [])[item.index] || [];
        item.units = key.map(([from, to, label]) => ({ from, to, label }));
        item.done = true;
        this.pending = null;
        this.render(item);
        item.actions.innerHTML = '';
        if (!silent) this.say(item, 'Lösung.', 'solution');
    }
}

export function initSyntaxMarking() {
    document.querySelectorAll('[data-ph-sm]').forEach((root) => {
        if (root.hasAttribute(READY)) return;
        root.setAttribute(READY, 'true');
        try {
            new SyntaxMarking(root);
        } catch (err) {
            console.error('[syntax-marking] init failed', err);
        }
    });
}
